import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, MessageCircle, Trash2, AlertTriangle, Plus, X, MinusCircle } from "lucide-react";
import * as XLSX from "xlsx";
import { api, fmt, rupiah, doughSplit, idDate, todayISO } from "../lib/dimsum";

export const GajiPeserta = ({ refreshKey, teams = [], adminUser = null, onChanged }) => {
  const [recaps, setRecaps] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [activePreset, setActivePreset] = useState("");
  const [overrides, setOverrides] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editVals, setEditVals] = useState({});
  const [savingOverride, setSavingOverride] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [loans, setLoans] = useState([]);
  const [lemburs, setLemburs] = useState([]);
  const [capPotongan, setCapPotongan] = useState(true);
  const [deductions, setDeductions] = useState([]);
  const [showPotonganForm, setShowPotonganForm] = useState(false);
  const [pForm, setPForm] = useState({ date: todayISO(), name: "", amount: "", note: "" });
  const [savingP, setSavingP] = useState(false);
  const [pNameOpen, setPNameOpen] = useState(false);

  const isMaster = adminUser?.role === "master";
  const isAdmin = !!adminUser;

  const isoDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const applyPreset = (p) => {
    const now = new Date();
    let s, e;
    if (p === "day") {
      s = new Date(now);
      e = new Date(now);
    } else if (p === "week") {
      const dow = (now.getDay() + 6) % 7; // Senin = 0
      const monday = new Date(now);
      monday.setDate(now.getDate() - dow);
      e = new Date(monday);
      e.setDate(monday.getDate() + 4); // Jumat pekan ini
      s = new Date(e);
      s.setDate(e.getDate() - 7); // Jumat pekan lalu
    } else if (p === "month") {
      s = new Date(now.getFullYear(), now.getMonth(), 1);
      e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else {
      s = new Date(now.getFullYear(), 0, 1);
      e = new Date(now.getFullYear(), 11, 31);
    }
    setStart(isoDate(s));
    setEnd(isoDate(e));
    setActivePreset(p);
  };

  useEffect(() => {
    const params = new URLSearchParams();
    if (start) params.append("start", start);
    if (end) params.append("end", end);
    api.get(`/recaps?${params.toString()}`).then((r) => setRecaps(r.data));
    api
      .get(`/salary-override`)
      .then((r) => {
        const m = {};
        (r.data || []).forEach((o) => (m[o.name] = o));
        setOverrides(m);
      })
      .catch(() => setOverrides({}));
    api.get(`/loans`).then((r) => setLoans(r.data || [])).catch(() => setLoans([]));
    api.get(`/lembur?${params.toString()}`).then((r) => setLemburs(r.data || [])).catch(() => setLemburs([]));
    api.get(`/deductions`).then((r) => setDeductions(r.data || [])).catch(() => setDeductions([]));
    setEditMode(false);
  }, [start, end, refreshKey]);

  useEffect(() => {
    applyPreset("week");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rateMap = useMemo(() => {
    const m = {};
    teams.forEach((t) => {
      const nm = (t.members[0] || t.name || "").trim().toLowerCase();
      m[nm] = {
        jumbo: t.rate_jumbo ?? 3250,
        mini: t.rate_mini ?? 6000,
        lemburJumbo: t.lembur_jumbo ?? 0,
        lemburMini: t.lembur_mini ?? 0,
        bonus: t.bonus_harian ?? 0,
      };
    });
    return m;
  }, [teams]);

  const inRange = (d) => (!start || d >= start) && (!end || d <= end);

  const loanMap = useMemo(() => {
    const m = {};
    loans.forEach((l) => {
      const k = (l.name || "").trim().toLowerCase();
      if (!m[k]) m[k] = { name: l.name, pinjam: 0, bayar: 0, rangeBayar: 0 };
      if (l.kind === "bayar") {
        m[k].bayar += l.amount || 0;
        if (inRange(l.date)) m[k].rangeBayar += l.amount || 0;
      } else {
        m[k].pinjam += l.amount || 0;
      }
    });
    return m;
  }, [loans, start, end]);

  const deductionMap = useMemo(() => {
    const m = {};
    deductions.forEach((x) => {
      if (!inRange(x.date)) return;
      const k = (x.name || "").trim().toLowerCase();
      if (!m[k]) m[k] = { total: 0, entries: [] };
      m[k].total += x.amount || 0;
      m[k].entries.push(x);
    });
    return m;
  }, [deductions, start, end]);

  const lemburMap = useMemo(() => {
    const m = {};
    lemburs.forEach((l) => {
      const names = (l.members || []).filter((x) => x && x.trim());
      if (names.length === 0) return;
      const share = 1 / names.length;
      (l.items || []).forEach((it) => {
        const kg = Number(it.kg) || 0;
        names.forEach((n) => {
          const k = n.trim().toLowerCase();
          if (!m[k]) m[k] = { name: n, jumboKg: 0, miniKg: 0 };
          if (it.kategori === "J") m[k].jumboKg += kg * share;
          else m[k].miniKg += kg * share;
        });
      });
    });
    return m;
  }, [lemburs]);

  const participantOptions = useMemo(
    () => teams.map((t) => t.members[0] || t.name).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [teams]
  );

  const rows = useMemo(() => {
    const map = {};
    recaps.forEach((r) => {
      const names = (r.members || []).filter((m) => m && m.trim());
      if (names.length === 0) return;
      const { jumbo, mini } = doughSplit(r);
      const share = 1 / names.length;
      names.forEach((n) => {
        if (!map[n]) map[n] = { name: n, jumbo: 0, mini: 0, days: new Set() };
        map[n].jumbo += jumbo * share;
        map[n].mini += mini * share;
        if (r.date) map[n].days.add(r.date);
      });
    });
    const existing = new Set(Object.keys(map).map((n) => n.trim().toLowerCase()));
    const inject = {};
    Object.keys(deductionMap).forEach((k) => {
      if (!existing.has(k)) inject[k] = deductionMap[k].entries[0]?.name || k;
    });
    Object.keys(loanMap).forEach((k) => {
      if (!existing.has(k) && loanMap[k].rangeBayar > 0) inject[k] = inject[k] || loanMap[k].name || k;
    });
    Object.keys(lemburMap).forEach((k) => {
      if (!existing.has(k) && (lemburMap[k].jumboKg > 0 || lemburMap[k].miniKg > 0))
        inject[k] = inject[k] || lemburMap[k].name || k;
    });
    Object.values(inject).forEach((nm) => {
      map[nm] = { name: nm, jumbo: 0, mini: 0, days: new Set() };
    });
    return Object.values(map)
      .map((p) => {
        const rate = rateMap[(p.name || "").trim().toLowerCase()] || { jumbo: 3250, mini: 6000, lemburJumbo: 0, lemburMini: 0, bonus: 0 };
        const ov = overrides[p.name] || {};
        const lem = lemburMap[(p.name || "").trim().toLowerCase()] || { jumboKg: 0, miniKg: 0 };
        const baseDays = p.days.size;
        const baseJumbo = p.jumbo;
        const baseMini = p.mini;
        const baseGaji = baseJumbo * rate.jumbo + baseMini * rate.mini;
        const baseLembur = lem.jumboKg * rate.lemburJumbo + lem.miniKg * rate.lemburMini;
        const days = ov.days != null ? ov.days : baseDays;
        const jumboKg = ov.jumbo != null ? ov.jumbo : baseJumbo;
        const miniKg = ov.mini != null ? ov.mini : baseMini;
        const gaji = ov.gaji != null ? ov.gaji : jumboKg * rate.jumbo + miniKg * rate.mini;
        const lembJ = lem.jumboKg * rate.lemburJumbo;
        const lembM = lem.miniKg * rate.lemburMini;
        const lembur = ov.lembur != null ? ov.lembur : lembJ + lembM;
        const bonus = days * (rate.bonus || 0);
        const gross = gaji + lembur + bonus;
        const lm = loanMap[(p.name || "").trim().toLowerCase()] || { pinjam: 0, bayar: 0, rangeBayar: 0 };
        const paid = lm.rangeBayar;
        const outstanding = Math.max(0, lm.pinjam - lm.bayar);
        const autoPotongan = paid;
        const potonganHutang = ov.potongan != null ? ov.potongan : autoPotongan;
        const dm = deductionMap[(p.name || "").trim().toLowerCase()] || { total: 0, entries: [] };
        const potonganManual = dm.total;
        const manualEntries = dm.entries;
        const potongan = potonganHutang + potonganManual;
        const detailTotal = gross - potongan;
        return {
          name: p.name, jumbo: jumboKg, mini: miniKg, days, gaji, lembJ, lembM, lembur, bonus, gross, outstanding, potongan, potonganHutang, potonganManual, manualEntries, autoPotongan, detailTotal, total: gross - potongan,
          baseDays, baseJumbo, baseMini, baseGaji, baseLembur,
          edited: ["days", "jumbo", "mini", "gaji", "lembur", "potongan"].some((k) => ov[k] != null),
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [recaps, rateMap, overrides, loanMap, capPotongan, deductionMap, lemburMap]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (s, p) => ({
          jumbo: s.jumbo + p.jumbo,
          mini: s.mini + p.mini,
          gaji: s.gaji + p.gaji,
          lembur: s.lembur + p.lembur,
          bonus: s.bonus + p.bonus,
          potongan: s.potongan + p.potongan,
          total: s.total + p.total,
        }),
        { jumbo: 0, mini: 0, gaji: 0, lembur: 0, bonus: 0, potongan: 0, total: 0 }
      ),
    [rows]
  );

  const totalDays = useMemo(
    () => new Set(recaps.map((r) => r.date).filter(Boolean)).size,
    [recaps]
  );

  const exportExcel = () => {
    const data = rows.map((p) => ({
      Nama: p.name,
      "Hari Kerja": p.days,
      "Adonan Jumbo (kg)": Number(p.jumbo.toFixed(2)),
      "Adonan Mini (kg)": Number(p.mini.toFixed(2)),
      "Gaji (Rp)": Math.round(p.gaji),
      "Lembur (Rp)": Math.round(p.lembur),
      "Bonus Harian (Rp)": Math.round(p.bonus),
      "Total (Rp)": Math.round(p.total),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Gaji Peserta");
    XLSX.writeFile(wb, `gaji-peserta${start && end ? `-${start}_sd_${end}` : ""}.xlsx`);
    toast.success("📊 Gaji berhasil diekspor ke Excel");
  };

  const shareWa = async () => {
    const tgl = start && end ? `${idDate(start)} - ${idDate(end)}` : "Semua tanggal";
    let text = "*DETAIL GAJI PESERTA*\n\n";
    if (rows.length === 0) {
      text += "Tidak ada data.";
    } else {
      rows.forEach((p) => {
        text += `Nama : ${p.name}\n`;
        text += `Tgl : ${tgl}\n`;
        text += `Jumbo : ${fmt(p.jumbo)} kg\n`;
        text += `Mini : ${fmt(p.mini)} kg\n`;
        if (p.lembJ > 0) text += `Lembur Jumbo : ${rupiah(p.lembJ)}\n`;
        if (p.lembM > 0) text += `Lembur Mini : ${rupiah(p.lembM)}\n`;
        text += `Bonus Absen Harian : ${rupiah(p.bonus)}\n`;
        if (p.potongan > 0) {
          text += `Gaji : ${rupiah(p.gross)} (Belum Dipotong)\n`;
        }
        if (p.potonganHutang > 0) {
          text += `Potong Hutang : -${Math.round(p.potonganHutang).toLocaleString("id-ID")}\n`;
          text += `Sisa Hutang : ${Math.round(Math.max(0, p.outstanding)).toLocaleString("id-ID")}\n`;
        }
        (p.manualEntries || []).forEach((e) => {
          text += `Potongan${e.note ? ` (${e.note})` : ""} : -${Math.round(e.amount).toLocaleString("id-ID")}\n`;
        });
        text += `*Total : ${rupiah(p.detailTotal)}*\n`;
        text += "-------------------\n";
      });
      const grand = rows.reduce((s, p) => s + p.detailTotal, 0);
      text += `\nTgl ${tgl}\n`;
      text += `*Total Gaji Keseluruhan : ${rupiah(grand)}*\n`;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const startEdit = () => {
    const v = {};
    rows.forEach((p) => {
      v[p.name] = {
        days: Number(p.days.toFixed(2)),
        jumbo: Number(p.jumbo.toFixed(2)),
        mini: Number(p.mini.toFixed(2)),
        gaji: Math.round(p.gaji),
        lembur: Math.round(p.lembur),
        potongan: Math.round(p.potonganHutang),
      };
    });
    setEditVals(v);
    setEditMode(true);
  };

  const setEditVal = (name, field, value) => {
    setEditVals((prev) => {
      const cur = { ...(prev[name] || {}) };
      cur[field] = value;
      if (field === "jumbo" || field === "mini") {
        const rate = rateMap[(name || "").trim().toLowerCase()] || { jumbo: 3250, mini: 6000, lemburJumbo: 0, lemburMini: 0 };
        const j = Number(cur.jumbo) || 0;
        const m = Number(cur.mini) || 0;
        cur.gaji = Math.round(j * rate.jumbo + m * rate.mini);
      }
      return { ...prev, [name]: cur };
    });
  };

  const saveOverride = async () => {
    setSavingOverride(true);
    try {
      const rowByName = {};
      rows.forEach((p) => (rowByName[p.name] = p));
      const num = (x) => (x === "" || x == null ? null : Number(x));
      const changed = (val, baseVal, factor) => {
        const n = num(val);
        if (n == null) return null;
        return Math.round(n * factor) === Math.round((baseVal || 0) * factor) ? null : n;
      };
      await Promise.all(
        Object.entries(editVals).map(([name, v]) => {
          const base = rowByName[name] || {};
          return api.put("/salary-override", {
            name,
            days: changed(v.days, base.baseDays, 100),
            jumbo: changed(v.jumbo, base.baseJumbo, 100),
            mini: changed(v.mini, base.baseMini, 100),
            gaji: changed(v.gaji, base.baseGaji, 1),
            lembur: changed(v.lembur, base.baseLembur, 1),
            potongan: changed(v.potongan, base.autoPotongan, 1),
          });
        })
      );
      const r = await api.get(`/salary-override`);
      const m = {};
      (r.data || []).forEach((o) => (m[o.name] = o));
      setOverrides(m);
      setEditMode(false);
      toast.success("✅ Gaji diperbarui & berlaku untuk semua rentang tanggal");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan (butuh login master)");
    } finally {
      setSavingOverride(false);
    }
  };

  const resetOverride = async () => {
    setSavingOverride(true);
    try {
      await api.delete(`/salary-override`);
      setOverrides({});
      setEditMode(false);
      toast.success("🔄 Gaji dikembalikan ke perhitungan otomatis");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mereset");
    } finally {
      setSavingOverride(false);
    }
  };

  const purgeParticipant = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/participants/${encodeURIComponent(deleteTarget)}`);
      const d = r.data || {};
      toast.success(`🗑️ "${deleteTarget}" dihapus (${d.deleted_recaps || 0} rekap dihapus, ${d.updated_recaps || 0} rekap diperbarui)`);
      setDeleteTarget(null);
      setConfirmText("");
      onChanged?.();
      const params = new URLSearchParams();
      if (start) params.append("start", start);
      if (end) params.append("end", end);
      const rr = await api.get(`/recaps?${params.toString()}`);
      setRecaps(rr.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus (khusus master)");
    } finally {
      setDeleting(false);
    }
  };

  const saveDeduction = async () => {
    if (!pForm.name.trim()) return toast.error("Pilih nama peserta");
    if (!(Number(pForm.amount) > 0)) return toast.error("Nominal harus lebih dari 0");
    if (!pForm.date) return toast.error("Isi tanggal pemotongan");
    setSavingP(true);
    try {
      await api.post("/deductions", { name: pForm.name.trim(), amount: Number(pForm.amount), date: pForm.date, note: pForm.note.trim() });
      toast.success("✅ Potongan gaji dicatat");
      setPForm({ date: todayISO(), name: "", amount: "", note: "" });
      const r = await api.get("/deductions");
      setDeductions(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan (butuh login admin/master)");
    } finally {
      setSavingP(false);
    }
  };

  const deleteDeduction = async (id) => {
    if (!window.confirm("Hapus potongan ini?")) return;
    try {
      await api.delete(`/deductions/${id}`);
      toast.success("🗑️ Potongan dihapus");
      const r = await api.get("/deductions");
      setDeductions(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const shareRowWa = (p) => {
    const tgl = start && end ? `${idDate(start)} - ${idDate(end)}` : "Semua tanggal";
    let text = "*SLIP GAJI PESERTA*\n\n";
    text += `Nama : ${p.name}\n`;
    text += `Periode : ${tgl}\n`;
    text += `Hari Kerja : ${p.days} hari\n`;
    text += `Jumbo : ${fmt(p.jumbo)} kg\n`;
    text += `Mini : ${fmt(p.mini)} kg\n`;
    text += `Gaji : ${rupiah(p.gaji)}\n`;
    text += `Lembur : ${rupiah(p.lembur)}\n`;
    text += `Bonus Harian : ${rupiah(p.bonus)}\n`;
    if (p.potonganHutang > 0) {
      text += `Potong Hutang : -${Math.round(p.potonganHutang).toLocaleString("id-ID")}\n`;
      text += `Sisa Hutang : ${Math.round(Math.max(0, p.outstanding)).toLocaleString("id-ID")}\n`;
    }
    (p.manualEntries || []).forEach((e) => {
      text += `Potongan${e.note ? ` (${e.note})` : ""} : -${Math.round(e.amount).toLocaleString("id-ID")}\n`;
    });
    text += `*GAJI BERSIH : ${rupiah(p.total)}*\n`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const inputCls =
    "w-full rounded-2xl border-2 border-stone-300 bg-white px-3 py-3 font-semibold focus:border-rose-500 focus:outline-none";

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-stone-900">Gaji Peserta</h2>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <button
              data-testid="btn-toggle-potongan-form"
              onClick={() => setShowPotonganForm((v) => !v)}
              className="min-h-[44px] rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-bold px-4 flex items-center gap-2 text-sm active:scale-95 transition-transform"
            >
              <MinusCircle className="w-4 h-4" /> Potongan
            </button>
          )}
          <button
            data-testid="btn-share-gaji-wa"
            onClick={shareWa}
            className="min-h-[44px] rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 flex items-center gap-2 text-sm active:scale-95 transition-transform"
          >
            <MessageCircle className="w-4 h-4" /> Kirim WA
          </button>
          <button
            data-testid="btn-export-gaji-excel"
            onClick={exportExcel}
            className="min-h-[44px] rounded-2xl bg-green-700 hover:bg-green-800 text-white font-bold px-4 flex items-center gap-2 text-sm active:scale-95 transition-transform"
          >
            <Download className="w-4 h-4" /> Excel
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          ["day", "Hari"],
          ["week", "Pekan"],
          ["month", "Bulan"],
          ["year", "Tahun"],
        ].map(([p, label]) => (
          <button
            key={p}
            data-testid={`gaji-filter-${p}`}
            onClick={() => applyPreset(p)}
            className={`min-h-[44px] rounded-2xl px-4 font-bold text-sm transition-colors ${
              activePreset === p
                ? "bg-rose-600 text-white shadow-md"
                : "bg-stone-100 text-stone-700 hover:bg-stone-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Dari Tanggal</label>
          <input data-testid="gaji-date-start" type="date" value={start} onChange={(e) => { setStart(e.target.value); setActivePreset(""); }} className={inputCls} />
        </div>
        <div>
          <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Sampai Tanggal</label>
          <input data-testid="gaji-date-end" type="date" value={end} onChange={(e) => { setEnd(e.target.value); setActivePreset(""); }} className={inputCls} />
        </div>
      </div>

      {showPotonganForm && (
        <div data-testid="potongan-form-panel" className="bg-white border-2 border-rose-200 rounded-3xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg text-stone-900 flex items-center gap-2">
              <MinusCircle className="w-5 h-5 text-rose-600" /> Potongan Gaji
            </h3>
            <button data-testid="potongan-form-close" onClick={() => setShowPotonganForm(false)} className="grid place-items-center w-8 h-8 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Tanggal Pemotongan</label>
              <input type="date" data-testid="potongan-date" value={pForm.date} onChange={(e) => setPForm((s) => ({ ...s, date: e.target.value }))} className={inputCls} />
            </div>
            <div className="relative">
              <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Nama Peserta</label>
              <input
                type="text"
                data-testid="potongan-name"
                value={pForm.name}
                placeholder="Ketik nama peserta…"
                onChange={(e) => { setPForm((s) => ({ ...s, name: e.target.value })); setPNameOpen(true); }}
                onFocus={() => setPNameOpen(true)}
                onBlur={() => setTimeout(() => setPNameOpen(false), 150)}
                className={inputCls}
              />
              {pNameOpen && pForm.name.trim().length >= 1 && (() => {
                const q = pForm.name.trim().toLowerCase();
                const matches = participantOptions.filter((nm) => nm.toLowerCase().includes(q) && nm !== pForm.name);
                return matches.length > 0 ? (
                  <ul data-testid="potongan-name-suggestions" className="absolute z-30 mt-1.5 w-full max-h-52 overflow-auto rounded-2xl border-2 border-stone-200 bg-white shadow-lg">
                    {matches.map((nm) => (
                      <li key={nm}>
                        <button
                          type="button"
                          data-testid={`potongan-name-option-${nm}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => { setPForm((s) => ({ ...s, name: nm })); setPNameOpen(false); }}
                          className="w-full text-left px-4 py-2.5 font-semibold text-stone-800 hover:bg-rose-50 transition-colors"
                        >
                          {nm}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null;
              })()}
            </div>
            <div>
              <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Nominal (Rp)</label>
              <input type="text" inputMode="numeric" data-testid="potongan-amount" value={pForm.amount ? Number(pForm.amount).toLocaleString("id-ID") : ""} onChange={(e) => setPForm((s) => ({ ...s, amount: e.target.value.replace(/\D/g, "") }))} placeholder="0" className={`${inputCls} font-mono-num`} />
            </div>
            <div>
              <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Keterangan</label>
              <input data-testid="potongan-note" value={pForm.note} onChange={(e) => setPForm((s) => ({ ...s, note: e.target.value }))} placeholder="mis. denda, kasbon, dll" className={inputCls} />
            </div>
          </div>
          <button data-testid="potongan-save" onClick={saveDeduction} disabled={savingP} className="inline-flex items-center gap-2 min-h-[44px] rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-bold px-4 text-sm disabled:opacity-50 active:scale-95 transition-transform">
            <Plus className="w-4 h-4" /> {savingP ? "Menyimpan…" : "Simpan Potongan"}
          </button>
          <div className="pt-2 border-t border-stone-100">
            <p className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">Daftar Potongan {start && end ? "(dalam rentang tanggal)" : ""}</p>
            {(() => {
              const list = deductions.filter((x) => inRange(x.date)).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
              return list.length === 0 ? (
                <p className="text-sm font-semibold text-stone-400">Belum ada potongan.</p>
              ) : (
                <div className="space-y-1.5" data-testid="potongan-list">
                  {list.map((x) => (
                    <div key={x.id} data-testid={`potongan-item-${x.id}`} className="flex items-center justify-between gap-2 rounded-xl bg-stone-50 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-stone-800 truncate">{x.name} <span className="font-mono-num text-rose-600">- {rupiah(x.amount)}</span></p>
                        <p className="text-xs text-stone-400 truncate">{idDate(x.date)}{x.note ? ` • ${x.note}` : ""}</p>
                      </div>
                      <button data-testid={`potongan-delete-${x.id}`} onClick={() => deleteDeduction(x.id)} className="grid place-items-center w-8 h-8 rounded-lg text-rose-600 hover:bg-rose-100 shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {isMaster && (
        <div className="flex flex-wrap items-center gap-2" data-testid="gaji-edit-toolbar">
          {!editMode ? (
            <button
              data-testid="gaji-edit-btn"
              onClick={startEdit}
              className="inline-flex items-center gap-2 min-h-[44px] rounded-2xl bg-stone-800 hover:bg-stone-900 text-white font-bold px-4 text-sm transition-colors"
            >
              Edit Gaji (Hari/Jumbo/Mini/Gaji/Lembur/Potongan)
            </button>
          ) : (
            <>
              <button
                data-testid="gaji-save-override-btn"
                onClick={saveOverride}
                disabled={savingOverride}
                className="inline-flex items-center gap-2 min-h-[44px] rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 text-sm disabled:opacity-50 transition-colors"
              >
                {savingOverride ? "Menyimpan…" : "Simpan Perubahan"}
              </button>
              <button
                data-testid="gaji-reset-override-btn"
                onClick={resetOverride}
                disabled={savingOverride}
                className="inline-flex items-center gap-2 min-h-[44px] rounded-2xl bg-amber-100 hover:bg-amber-200 text-amber-800 font-bold px-4 text-sm disabled:opacity-50 transition-colors"
              >
                Reset Otomatis
              </button>
              <button
                data-testid="gaji-cancel-override-btn"
                onClick={() => setEditMode(false)}
                disabled={savingOverride}
                className="inline-flex items-center gap-2 min-h-[44px] rounded-2xl bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold px-4 text-sm disabled:opacity-50 transition-colors"
              >
                Batal
              </button>
            </>
          )}
        </div>
      )}

      <div className="w-full overflow-x-auto rounded-3xl border-2 border-stone-200 bg-white">
        <table data-testid="gaji-table" className="w-full text-left border-collapse text-xs sm:text-base">
          <thead>
            <tr className="bg-stone-100 text-stone-700 text-xs uppercase tracking-wider">
              <th className="py-3 px-2 sm:px-4 font-extrabold">Nama Peserta</th>
              <th className="py-3 px-2 sm:px-4 font-extrabold">Hari Kerja</th>
              <th className="py-3 px-2 sm:px-4 font-extrabold">Jumbo</th>
              <th className="py-3 px-2 sm:px-4 font-extrabold">Mini</th>
              <th className="py-3 px-2 sm:px-4 font-extrabold">Gaji</th>
              <th className="py-3 px-2 sm:px-4 font-extrabold">Lembur</th>
              <th className="py-3 px-2 sm:px-4 font-extrabold">Bonus</th>
              <th className="py-3 px-2 sm:px-4 font-extrabold">Potongan</th>
              <th className="py-3 px-2 sm:px-4 font-extrabold">Bayaran Bersih</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.name} data-testid="gaji-row" className="border-t border-stone-100 hover:bg-rose-50/40 transition-colors">
                <td className="py-3 px-2 sm:px-4">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-stone-900">{p.name}</span>
                    {p.edited && !editMode && (
                      <span data-testid="gaji-edited-badge" className="inline-block rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 align-middle">diedit</span>
                    )}
                    {!editMode && (
                      <button
                        data-testid={`gaji-wa-${p.name}`}
                        onClick={() => shareRowWa(p)}
                        title="Kirim slip gaji ke WhatsApp"
                        className="grid place-items-center w-7 h-7 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 shrink-0 transition-colors"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {!editMode && isMaster && (
                      <button
                        data-testid={`gaji-delete-${p.name}`}
                        onClick={() => { setDeleteTarget(p.name); setConfirmText(""); }}
                        title="Hapus peserta & seluruh datanya"
                        className="grid place-items-center w-7 h-7 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 shrink-0 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
                <td className="py-3 px-2 sm:px-4 font-mono-num font-bold text-stone-700 whitespace-nowrap">
                  {editMode ? (
                    <input type="number" data-testid={`gaji-edit-days-${p.name}`} value={editVals[p.name]?.days ?? ""} onChange={(e) => setEditVal(p.name, "days", e.target.value)} className="w-20 rounded-lg border-2 border-stone-200 px-2 py-1 font-mono-num" />
                  ) : (
                    `${p.days} hari`
                  )}
                </td>
                <td className="py-3 px-2 sm:px-4 font-mono-num font-bold text-orange-700 whitespace-nowrap">
                  {editMode ? (
                    <input type="number" data-testid={`gaji-edit-jumbo-${p.name}`} value={editVals[p.name]?.jumbo ?? ""} onChange={(e) => setEditVal(p.name, "jumbo", e.target.value)} className="w-24 rounded-lg border-2 border-stone-200 px-2 py-1 font-mono-num" />
                  ) : (
                    `${fmt(p.jumbo)} kg`
                  )}
                </td>
                <td className="py-3 px-2 sm:px-4 font-mono-num font-bold text-amber-600 whitespace-nowrap">
                  {editMode ? (
                    <input type="number" data-testid={`gaji-edit-mini-${p.name}`} value={editVals[p.name]?.mini ?? ""} onChange={(e) => setEditVal(p.name, "mini", e.target.value)} className="w-24 rounded-lg border-2 border-stone-200 px-2 py-1 font-mono-num" />
                  ) : (
                    `${fmt(p.mini)} kg`
                  )}
                </td>
                <td className="py-3 px-2 sm:px-4 font-mono-num font-bold text-stone-800 whitespace-nowrap">
                  {editMode ? (
                    <input type="number" data-testid={`gaji-edit-gaji-${p.name}`} value={editVals[p.name]?.gaji ?? ""} onChange={(e) => setEditVal(p.name, "gaji", e.target.value)} className="w-28 rounded-lg border-2 border-stone-200 px-2 py-1 font-mono-num" />
                  ) : (
                    rupiah(p.gaji)
                  )}
                </td>
                <td className="py-3 px-2 sm:px-4 font-mono-num font-bold text-sky-700 whitespace-nowrap">
                  {editMode ? (
                    <input type="number" data-testid={`gaji-edit-lembur-${p.name}`} value={editVals[p.name]?.lembur ?? ""} onChange={(e) => setEditVal(p.name, "lembur", e.target.value)} className="w-28 rounded-lg border-2 border-stone-200 px-2 py-1 font-mono-num" />
                  ) : (
                    rupiah(p.lembur)
                  )}
                </td>
                <td className="py-3 px-2 sm:px-4 font-mono-num font-bold text-violet-700 whitespace-nowrap">
                  {rupiah(p.bonus)}
                </td>
                <td className="py-3 px-2 sm:px-4 font-mono-num font-bold text-rose-600 whitespace-nowrap">
                  {editMode ? (
                    <input type="number" data-testid={`gaji-edit-potongan-${p.name}`} value={editVals[p.name]?.potongan ?? ""} onChange={(e) => setEditVal(p.name, "potongan", e.target.value)} className="w-28 rounded-lg border-2 border-stone-200 px-2 py-1 font-mono-num" />
                  ) : (
                    p.potongan > 0 ? `- ${rupiah(p.potongan)}` : rupiah(0)
                  )}
                </td>
                <td className="py-3 px-2 sm:px-4 font-mono-num font-extrabold text-emerald-700 whitespace-nowrap">
                  {editMode
                    ? rupiah((Number(editVals[p.name]?.gaji) || 0) + (Number(editVals[p.name]?.lembur) || 0) + (Number(editVals[p.name]?.days) || 0) * ((rateMap[(p.name || "").trim().toLowerCase()]?.bonus) || 0) - (Number(editVals[p.name]?.potongan) || 0) - (p.potonganManual || 0))
                    : rupiah(p.total)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className="py-10 text-center text-stone-400 font-semibold">Belum ada data gaji.</td>
              </tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-stone-200 bg-stone-50 font-extrabold">
                <td className="py-3 px-2 sm:px-4 text-stone-900">TOTAL</td>
                <td className="py-3 px-2 sm:px-4 font-mono-num text-stone-700 whitespace-nowrap">{totalDays} hari</td>
                <td className="py-3 px-2 sm:px-4 font-mono-num text-orange-700 whitespace-nowrap">{fmt(totals.jumbo)} kg</td>
                <td className="py-3 px-2 sm:px-4 font-mono-num text-amber-600 whitespace-nowrap">{fmt(totals.mini)} kg</td>
                <td className="py-3 px-2 sm:px-4 font-mono-num text-stone-800 whitespace-nowrap">{rupiah(totals.gaji)}</td>
                <td className="py-3 px-2 sm:px-4 font-mono-num text-sky-700 whitespace-nowrap">{rupiah(totals.lembur)}</td>
                <td className="py-3 px-2 sm:px-4 font-mono-num text-violet-700 whitespace-nowrap">{rupiah(totals.bonus)}</td>
                <td className="py-3 px-2 sm:px-4 font-mono-num text-rose-600 whitespace-nowrap">{totals.potongan > 0 ? `- ${rupiah(totals.potongan)}` : rupiah(0)}</td>
                <td className="py-3 px-2 sm:px-4 font-mono-num text-emerald-700 whitespace-nowrap">{rupiah(totals.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p className="text-sm font-semibold text-stone-500">
        Gaji = kg adonan × tarif; Lembur = kg lembur (dari tab Input Lembur) × tarif lembur (J pakai tarif Lembur Jumbo, M pakai tarif Lembur Mini). Bonus = hari kerja ×
        bonus harian. Potongan = pembayaran hutang (Bayar) + potongan manual (tombol Potongan). Total = Gaji + Lembur + Bonus − Potongan.
        Semua tarif diatur di tab Kelola Peserta. Setiap rekap & lembur dibagi rata ke jumlah peserta.
      </p>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="gaji-delete-dialog">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="grid place-items-center w-11 h-11 rounded-2xl bg-rose-100 text-rose-600 shrink-0">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-lg text-stone-900">Hapus Peserta Permanen</h3>
            </div>
            <p className="text-sm font-semibold text-stone-600">
              Menghapus <span className="font-extrabold text-rose-600">{deleteTarget}</span> akan menghapus
              peserta ini beserta <span className="font-bold">seluruh data rekapnya</span>. Peserta akan hilang
              total dari Gaji & Riwayat. Tindakan ini <span className="font-bold">tidak bisa dibatalkan</span>.
            </p>
            <div>
              <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">
                Ketik nama peserta untuk konfirmasi
              </label>
              <input
                data-testid="gaji-delete-confirm-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={deleteTarget}
                className="w-full min-h-[44px] rounded-xl border-2 border-stone-200 px-3 font-semibold focus:border-rose-500 focus:outline-none"
              />
            </div>
            <div className="flex gap-2">
              <button
                data-testid="gaji-delete-confirm-btn"
                onClick={purgeParticipant}
                disabled={deleting || confirmText.trim().toLowerCase() !== deleteTarget.trim().toLowerCase()}
                className="flex-1 inline-flex items-center justify-center gap-2 min-h-[44px] rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 className="w-4 h-4" /> {deleting ? "Menghapus…" : "Hapus Permanen"}
              </button>
              <button
                onClick={() => { setDeleteTarget(null); setConfirmText(""); }}
                disabled={deleting}
                className="min-h-[44px] px-4 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold transition-colors"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
